import type { IVideo } from "@aix/vidmax-shared/src/constants/prismaTypings"
import { EVideoTemplate } from "@aix/vidmax-shared/src/remotion/schema/project"
import { ELEVENLABS_DEFAULT_VOICE_ID } from "@aix/vidmax-shared/src/service/elevenlabs/defaultVoices"
import { createRedditStoryProjectProps } from "@aix/vidmax-shared/src/utils/projectProps"
import { isRedditVideo } from "@aix/vidmax-shared/src/utils/validation"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { VideoEditorHeader } from "~/components/Templates/Common/VideoEditorHeader"
import VideoEditorPage from "~/components/Templates/Common/VideoEditorPage"
import { RedditStoryEditorProvider } from "~/components/Templates/RedditStory/RedditStoryEditorContext"
import { useRedditStoryEditor } from "~/components/Templates/RedditStory/useRedditStoryEditor"
import { RedditStoryEditorPlayground } from "~/components/Templates/RedditStory/RedditStoryEditorPlayground"
import { api } from "~/utils/api"

const RedditStoryEditorPage = () => {
  const { videoId } = Route.useParams()

  // Fetch video data
  const {
    data: video,
    isLoading,
    error,
  } = api.video.getVideoById.useQuery(
    {
      id: videoId,
    },
    {
      enabled: !!videoId,
      refetchOnWindowFocus: false,
    },
  )

  // Create project props for the editor
  const projectProps = useMemo(() => {
    if (!video) return null
    const projectProps = createRedditStoryProjectProps(video)
    return { ...projectProps, voiceId: video.voiceId || ELEVENLABS_DEFAULT_VOICE_ID }
  }, [video])

  if (!video || !projectProps || !isRedditVideo(video)) {
    return (
      <VideoEditorPage
        video={video}
        isLoading={isLoading}
        error={error}
        expectedTemplateType={EVideoTemplate.REDDIT_STORY}
      />
    )
  }

  return (
    <RedditStoryEditorProvider video={video} projectProps={projectProps}>
      <RedditStoryEditorContent />
    </RedditStoryEditorProvider>
  )
}

// Component that uses the RedditStoryEditor context
const RedditStoryEditorContent = () => {
  const {
    video,
    renderStatus,
    saveChanges,
    isUpdating,
    undo,
    redo,
    history,
    saveAndRender,
    isDurationValid,
    estimatedDuration,
    checkDurationBeforeAction,
    hasChanges,
  } = useRedditStoryEditor()

  // Function to get the video title
  const getTitleText = (video: IVideo) => {
    return video.title || "Reddit Story"
  }

  useEffect(() => {
    // Add styles to <html> tag
    const html = document.documentElement
    const previousStyle = html.getAttribute("style")

    html.style.overflow = "hidden"

    // Optional: cleanup on unmount
    return () => {
      if (previousStyle !== null) {
        html.setAttribute("style", previousStyle)
      } else {
        html.removeAttribute("style")
      }
    }
  }, [])

  return (
    <VideoEditorPage
      video={video}
      isLoading={false}
      error={null}
      expectedTemplateType={EVideoTemplate.REDDIT_STORY}
      renderStatus={renderStatus}
      getTitleText={getTitleText}
    >
      <div className="flex flex-col h-screen">
        <VideoEditorHeader
          title={video?.title || "Reddit Story Editor"}
          video={video}
          saveChanges={saveChanges}
          isUpdating={isUpdating}
          undo={undo}
          redo={redo}
          canUndo={history.past.length > 0}
          canRedo={history.future.length > 0}
          saveAndRender={saveAndRender}
          renderStatus={renderStatus}
          isDurationValid={isDurationValid}
          estimatedDuration={estimatedDuration}
          checkDurationBeforeAction={checkDurationBeforeAction}
          hasChanges={hasChanges}
        />
        <RedditStoryEditorPlayground />
      </div>
    </VideoEditorPage>
  )
}

export const Route = createFileRoute("/videos/$videoId/editor/reddit-story")({
  component: RedditStoryEditorPage,
})
